import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import useHandleError from '@/hooks/useHandleError';
import type { User } from '@/pages/Administration/Users';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

export const useUserDeleteDialog = (userId: number) => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const [deleteMode, setDeleteMode] = useState<'one' | 'all'>(
    selectedOrganization.id === 0 ? 'all' : 'one'
  );
  const { data, error, isError, isFetching } = useQuery<User, AxiosError>({
    queryKey: [`users/${userId}`]
  });

  useEffect(() => {
    if (isError) {
      handleError(error, 'Failure to fetch user info.');
    }
  }, [isError]);

  const userOrganizations = useMemo(
    () => data?.organizations.map((org) => org.name) || [],
    [data]
  );

  const deleteUser = useMutation({
    mutationFn: ({ id }: User) => {
      if (userOrganizations.length === 1 || deleteMode === 'all') {
        return axiosClient.delete(`/users/${id}`);
      }
      return axiosClient.delete(
        `/users/${id}/organizations/${selectedOrganization.id}`
      );
    },
    onError: (err: AxiosError) => {
      handleError(err, 'Failure to delete user.');
    }
  });

  return {
    data,
    deleteMode,
    deleteUser,
    isFetching,
    selectedOrganization,
    setDeleteMode,
    userOrganizations
  };
};

export default useUserDeleteDialog;
